import { CheckCircleOutlined, ClockCircleOutlined, SyncOutlined } from '@ant-design/icons'
import { Col, Row, Select, Statistic } from 'antd'
import { ArrowLeft, CreditCard, Receipt, Wallet } from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import React, { useEffect, useState } from 'react'
import { RiErrorWarningFill } from 'react-icons/ri'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../../../../services/api'
import { formatCard, formatReal } from '../../../../../../utils/formatters'
import { generateUniqueId } from '../../../../../../utils/validators'
import {
  BackButton,
  CartContainer,
  CartHeader,
  CartTitle,
  ErrorContainer,
  LoadingContainer,
  PaymentAmount,
  PaymentContainer,
  PaymentMethodIcon,
  PaymentMethodInfo,
  PaymentOption,
  PaymentSelector,
  SelectedMethodHeader
} from '../styles'
import { clearOrderId, getOrderId } from '../utils/orderStorage'

const { Option } = Select

// Styled Components for Payment Forms
const PixContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`

const CountDownContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  max-width: 300px;
  padding: 10px 30px;
  margin-bottom: 40px;
  text-align: center;
  font-weight: 600;
  color: #02b0bb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`

const CountDownContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const QrCodeContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 20px;
  }
`

const QrCodeInfos = styled.div`
  margin-left: 30px;
  font-size: 14px;
  color: #777777;

  p {
    padding-bottom: 10px;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 30px;
  }
`

const ButtonCopy = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 200px;
  height: 40px;
  margin-top: 30px;
  background-color: #02b0bb;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  text-align: center;
  border-radius: 8px;
  border: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #018a94;
  }
`

const ButtonCopied = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 200px;
  height: 40px;
  margin-top: 30px;
  background-color: #6b7280;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  border-radius: 8px;
`

// Styled Components for Boleto
const BoletoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`

const BoletoInfoBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  margin-bottom: 20px;
`

const BoletoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
  text-align: center;
`

const BoletoSection = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`

const BoletoLabel = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 8px;
`

const BoletoLineContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
`

const BoletoLine = styled.code`
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #374151;
  word-break: break-all;
`

const CopyButton = styled.button`
  background: #02b0bb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background: #018a94;
  }
`

const BarcodeImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  margin: 16px auto;
  display: block;
`

const BoletoButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 300px;
  margin-top: 20px;
`

const BoletoPrimaryButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #02b0bb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background: #018a94;
    color: white;
  }
`

const BoletoSecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  color: #02b0bb;
  border: 2px solid #02b0bb;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #02b0bb;
    color: white;
  }
`

const BoletoExpirationInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;

  p {
    font-size: 0.875rem;
    color: #92400e;
    margin: 0;
  }
`

const BoletoInstructions = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #f0f9ff;
  border-radius: 8px;

  h4 {
    font-size: 0.875rem;
    font-weight: 600;
    color: #0369a1;
    margin-bottom: 12px;
  }

  ol {
    margin: 0;
    padding-left: 20px;

    li {
      font-size: 0.875rem;
      color: #0c4a6e;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`

const CreditCardContainer = styled.div`
  margin-top: 10px;
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;

  input {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    height: 44px;
    font-weight: 400;
    padding-left: 12px;
    font-size: 1rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #02b0bb;
      box-shadow: 0 0 0 3px rgba(2, 176, 187, 0.1);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  select {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    height: 44px;
    font-weight: 400;
    padding-left: 12px;
    font-size: 1rem;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #02b0bb;
    }
  }

  span {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
    color: #374151;
    margin-bottom: 6px;
  }

  .input-column {
    display: flex;
    flex-direction: column;

    input {
      margin-bottom: 16px;
    }
  }

  .input-groups {
    display: flex;
    gap: 16px;

    div {
      display: flex;
      flex-direction: column;
      width: 100%;

      input,
      select {
        margin-bottom: 16px;
      }
    }
  }

  .ant-select-selector {
    border-radius: 8px !important;
    border-color: #d1d5db !important;
    box-shadow: none !important;
    height: 44px !important;
    display: flex;
    align-items: center;

    &:hover {
      border-color: #02b0bb !important;
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 1.5rem;
`

const BtnFinishOrder = styled.button`
  background: #02b0bb;
  border: none;
  border-radius: 8px;
  height: 48px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #018a94;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const OptionInstallment = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  * {
    font-family: 'Nunito', sans-serif;
    color: rgba(61, 61, 61, 1);
  }

  .not_installment {
    color: #02b0bb;
  }
`

const StatusModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const StatusModalContent = styled.div<{ variant?: 'success' | 'error' }>`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;

  .icon-box {
    font-size: 48px;
    color: ${(props) => (props.variant === 'error' ? '#dc2626' : '#02b0bb')};
    margin-bottom: 1rem;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  p {
    color: #6b7280;
    margin-bottom: 1.5rem;
  }

  button {
    background: ${(props) => (props.variant === 'error' ? '#dc2626' : '#02b0bb')};
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }
  }
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #02b0bb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  p {
    color: #6b7280;
    font-size: 1rem;
  }
`

// Interfaces
interface PaymentProps {
  onPaymentComplete?: (orderId?: number) => void
  onBack?: () => void
}

interface OrderData {
  id: number
  uuid: string
  amount: string
  items: any[]
  apply_pix_discount: boolean
  shippingAddress?: any
  billingAddress?: any
}

interface CreditCardData {
  card_name?: string
  card_number?: string
  card_month_validate?: string
  card_year_validate?: string
  card_cvv: string
  installments?: number
}

type CreditCardFields =
  | 'card_name'
  | 'card_number'
  | 'card_month_validate'
  | 'card_year_validate'
  | 'card_cvv'
  | 'installments'

// Status Modal Component
function StatusModal({
  isOpen,
  onClose,
  title,
  text,
  icon,
  textButton,
  variant = 'success',
  onClickButton
}: {
  isOpen: boolean
  onClose: () => void
  title?: string
  text?: string
  icon: React.ReactNode
  textButton: string
  variant?: 'success' | 'error'
  onClickButton: () => void
}) {
  if (!isOpen) return null

  return (
    <StatusModalOverlay onClick={onClose}>
      <StatusModalContent variant={variant} onClick={(e) => e.stopPropagation()}>
        <div className="icon-box">{icon}</div>
        <h2>{title}</h2>
        <p>{text}</p>
        <button onClick={onClickButton}>{textButton}</button>
      </StatusModalContent>
    </StatusModalOverlay>
  )
}

// CountDown Component for Pix
function CountDown({ onExpire }: { onExpire: () => void }) {
  const { Countdown } = Statistic
  const time = Date.now() + (1000 * 60 * 60 * 24 * 2) / 580 // ~5 minutos

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Countdown value={time} onFinish={onExpire} />
      </Col>
    </Row>
  )
}

// Pix Payment Component
function PixPayment({
  finishOrderByPaymentMethod,
  loadingPayment,
  onSuccess,
  onError
}: {
  finishOrderByPaymentMethod: (params: { type: string }) => Promise<any>
  loadingPayment: boolean
  onSuccess?: () => void
  onError?: (message?: string) => void
}) {
  const [lineIsCopied, setLineIsCopied] = useState(false)
  const [pixInfo, setPixInfo] = useState<string>('')
  const [chargeId, setChargeId] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const copyByClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setLineIsCopied(true)
    toast.success('Código Pix copiado!')
  }

  const handleFinishOrder = async () => {
    try {
      setLoading(true)
      const data = await finishOrderByPaymentMethod({ type: 'pix' })
      const qrCode = data?.paymentInfos?.pix_qr_code || ''
      setPixInfo(qrCode)
      setChargeId(data?.paymentInfos?.charge_id || '')
    } catch (error) {
      console.error('Erro ao gerar Pix:', error)
      toast.error('Erro ao gerar o código Pix')
      onError?.('Erro ao gerar o código Pix. Por favor, tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleExpire = () => {
    setLineIsCopied(false)
    handleFinishOrder()
  }

  // Polling para verificar status do pagamento
  useEffect(() => {
    if (!chargeId) return undefined

    const checkPaymentStatus = async () => {
      try {
        const orderId = getOrderId()
        if (!orderId) return

        const response = await api.get(`/payments/charge/${orderId}/${chargeId}`)
        if (response.data?.order_paid) {
          toast.success('Pagamento confirmado!')
          clearOrderId()
          onSuccess?.()
        }
      } catch (error) {
        console.error('Erro ao verificar status:', error)
      }
    }

    const interval = setInterval(checkPaymentStatus, 5000) // Verifica a cada 5 segundos

    return () => clearInterval(interval)
  }, [chargeId, onSuccess])

  useEffect(() => {
    handleFinishOrder()
  }, [])

  if (loading || loadingPayment) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Gerando código Pix...</p>
      </LoadingOverlay>
    )
  }

  return (
    <PixContainer>
      <CountDownContainer>
        <ClockCircleOutlined style={{ fontSize: '24px', paddingRight: '10px' }} />
        <CountDownContent>
          <CountDown onExpire={handleExpire} />
          <p>para expirar</p>
        </CountDownContent>
      </CountDownContainer>

      <QrCodeContainer>
        <QRCodeCanvas value={pixInfo} size={200} bgColor="#f6f6f6" fgColor="#000000" level="L" includeMargin={false} />
        <QrCodeInfos>
          <p>1. Copie a chave pix.</p>
          <p>2. Entre no aplicativo da sua instituição financeira e acesse o ambiente pix.</p>
          <p>3. Escolha a opção de Copia e Cola e insira o código Pix.</p>
          <p>4. Verifique as informações e confirme o pagamento.</p>
          <p>5. Após validado o pagamento você será automaticamente direcionado para a página de resumo de compra.</p>
        </QrCodeInfos>
      </QrCodeContainer>

      {!lineIsCopied ? (
        <ButtonCopy onClick={() => copyByClipboard(pixInfo)}>Copiar código</ButtonCopy>
      ) : (
        <ButtonCopied>Código copiado</ButtonCopied>
      )}
    </PixContainer>
  )
}

// Credit Card Payment Component
function CreditCardPayment({
  order,
  finishOrderByPaymentMethod,
  loadingPayment,
  onSuccess,
  onError
}: {
  order: OrderData
  finishOrderByPaymentMethod: (params: { type: string; creditCard?: CreditCardData }) => Promise<any>
  loadingPayment: boolean
  onSuccess?: () => void
  onError?: (message?: string) => void
}) {
  const [error, setError] = useState(false)
  const [installments, setInstallments] = useState<any[]>([])
  const [loadingInstallments, setLoadingInstallments] = useState(false)
  const [spinner, setSpinner] = useState(false)

  const currentYear = new Date().getFullYear()
  const [creditCard, setCreditCard] = useState<CreditCardData>({
    installments: 1,
    card_month_validate: '01',
    card_year_validate: currentYear.toString(),
    card_cvv: ''
  })

  const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

  const getYears = () => {
    const years: number[] = []
    const startYear = new Date().getFullYear()
    for (let i = startYear; i <= 2050; i += 1) {
      years.push(i)
    }
    return years
  }

  const years = getYears()

  const handlePaymentCreditCard = async () => {
    try {
      const { paymentInfos, error: paymentError } = await finishOrderByPaymentMethod({
        type: 'credit_card',
        creditCard
      })

      if (paymentInfos?.status === 'paid') {
        clearOrderId()
        onSuccess?.()
      } else if (paymentInfos?.status === 'refused') {
        setError(true)
        onError?.('Seu pagamento foi recusado. Por favor, verifique os dados do cartão e tente novamente.')
      } else if (!paymentError) {
        clearOrderId()
        onSuccess?.()
      }
    } catch (e) {
      setError(true)
      onError?.('Ocorreu um erro ao processar o pagamento. Por favor, tente novamente.')
    }
  }

  const changeCreditCardInfo = (fieldToChange: CreditCardFields, newValue: string) => {
    setCreditCard({
      ...creditCard,
      [fieldToChange]: newValue
    })
  }

  const getInstallments = async () => {
    setLoadingInstallments(true)
    try {
      const { data } = await api.get(`/payments/installments/${order.id}`)
      setInstallments(data?.installments || [])
    } catch (error) {
      console.error('Erro ao buscar parcelas:', error)
    } finally {
      setLoadingInstallments(false)
      setSpinner(false)
    }
  }

  const reloadInstallments = async () => {
    setSpinner(true)
    await getInstallments()
  }

  useEffect(() => {
    changeCreditCardInfo('installments', '1')
  }, [])

  useEffect(() => {
    if (order?.id) {
      getInstallments()
    }
  }, [order])

  if (loadingPayment) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Processando pagamento...</p>
      </LoadingOverlay>
    )
  }

  return (
    <CreditCardContainer>
      <FormContainer>
        <div className="input-column">
          <span>Nome no cartão</span>
          <input
            type="text"
            id="card_name"
            required
            placeholder="Nome como está no cartão"
            value={creditCard?.card_name || ''}
            onChange={(e) => changeCreditCardInfo('card_name', e.target.value)}
          />

          <span>N° do cartão</span>
          <input
            type="text"
            id="card_number"
            required
            maxLength={19}
            placeholder="0000 0000 0000 0000"
            value={formatCard(creditCard?.card_number)}
            onChange={(e) => changeCreditCardInfo('card_number', e.target.value)}
          />
        </div>

        <div className="input-groups">
          <div>
            <span>Mês</span>
            <select
              required
              value={creditCard?.card_month_validate}
              onChange={(e) => changeCreditCardInfo('card_month_validate', e.target.value)}>
              {months.map((month) => (
                <option key={generateUniqueId(month)} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span>Ano</span>
            <select
              required
              value={creditCard?.card_year_validate}
              onChange={(e) => changeCreditCardInfo('card_year_validate', e.target.value)}>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year.toString().substr(-2)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span>CVV</span>
            <input
              type="text"
              id="card_cvv"
              minLength={3}
              maxLength={4}
              required
              placeholder="000"
              value={creditCard?.card_cvv}
              onChange={(e) => changeCreditCardInfo('card_cvv', e.target.value)}
            />
          </div>
        </div>

        <div className="input-column">
          <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginBottom: 0 }}>Parcelamento</span>
            <SyncOutlined
              spin={spinner}
              style={{ marginLeft: '10px', cursor: 'pointer', color: '#02b0bb' }}
              onClick={() => reloadInstallments()}
            />
          </div>

          <Select
            placeholder="Selecione a quantidade de parcelas"
            size="large"
            virtual={false}
            loading={loadingInstallments}
            onChange={(installment) => changeCreditCardInfo('installments', installment)}>
            {installments.map((installment: any) => (
              <Option key={generateUniqueId(installment?.id)} value={installment?.id}>
                <OptionInstallment>
                  <p className="values_installment">
                    {`${installment?.installment}x de ${formatReal(installment?.amount)}`}
                  </p>
                  {installment?.fees === '(sem juros)' ? (
                    <p className="total_installment not_installment">Sem juros</p>
                  ) : (
                    <p className="total_installment">{formatReal(installment?.total)}</p>
                  )}
                </OptionInstallment>
              </Option>
            ))}
          </Select>
        </div>
      </FormContainer>

      <ButtonContainer>
        <BtnFinishOrder onClick={handlePaymentCreditCard} disabled={creditCard.card_cvv.length < 3}>
          Finalizar pedido
        </BtnFinishOrder>
      </ButtonContainer>

      <StatusModal
        onClickButton={() => setError(false)}
        variant="error"
        icon={<RiErrorWarningFill />}
        textButton="Tentar Novamente"
        isOpen={error}
        onClose={() => setError(false)}
        title="Houve um erro ao processar seu pagamento"
        text="Por favor, revise seus dados e tente novamente"
      />
    </CreditCardContainer>
  )
}

// Boleto Payment Component
function BoletoPayment({
  finishOrderByPaymentMethod,
  loadingPayment,
  onSuccess,
  onError
}: {
  finishOrderByPaymentMethod: (params: { type: string }) => Promise<any>
  loadingPayment: boolean
  onSuccess?: () => void
  onError?: (message?: string) => void
}) {
  const [lineIsCopied, setLineIsCopied] = useState(false)
  const [boletoInfo, setBoletoInfo] = useState<{
    line: string
    barcode: string
    pdf: string
    dueDate: string
  } | null>(null)
  const [loading, setLoading] = useState(true)

  const copyByClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setLineIsCopied(true)
    toast.success('Linha digitável copiada!')
    setTimeout(() => setLineIsCopied(false), 3000)
  }

  const handleGenerateBoleto = async () => {
    try {
      setLoading(true)
      const data = await finishOrderByPaymentMethod({ type: 'boleto' })

      if (data?.paymentInfos) {
        setBoletoInfo({
          line: data.paymentInfos.boleto_line || '',
          barcode: data.paymentInfos.boleto_barcode || '',
          pdf: data.paymentInfos.boleto_pdf || '',
          dueDate: data.paymentInfos.boleto_due_date || ''
        })
      }
    } catch (error) {
      console.error('Erro ao gerar Boleto:', error)
      toast.error('Erro ao gerar o boleto')
      onError?.('Erro ao gerar o boleto. Por favor, tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const formatDueDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const handleFinishAndRedirect = () => {
    clearOrderId()
    onSuccess?.()
  }

  useEffect(() => {
    handleGenerateBoleto()
  }, [])

  if (loading || loadingPayment) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Gerando boleto...</p>
      </LoadingOverlay>
    )
  }

  if (!boletoInfo) {
    return (
      <ErrorContainer>
        <p>Erro ao carregar informações do boleto</p>
        <BoletoSecondaryButton onClick={handleGenerateBoleto}>Tentar novamente</BoletoSecondaryButton>
      </ErrorContainer>
    )
  }

  return (
    <BoletoContainer>
      <BoletoInfoBox>
        <BoletoTitle>Boleto Gerado com Sucesso!</BoletoTitle>

        <BoletoSection>
          <BoletoLabel>Linha digitável:</BoletoLabel>
          <BoletoLineContainer>
            <BoletoLine>{boletoInfo.line}</BoletoLine>
            <CopyButton onClick={() => copyByClipboard(boletoInfo.line)}>
              {lineIsCopied ? 'Copiado!' : 'Copiar'}
            </CopyButton>
          </BoletoLineContainer>
        </BoletoSection>

        {boletoInfo.barcode && (
          <BoletoSection>
            <BoletoLabel>Código de barras:</BoletoLabel>
            <BarcodeImage src={boletoInfo.barcode} alt="Código de barras do boleto" />
          </BoletoSection>
        )}

        {boletoInfo.dueDate && (
          <BoletoExpirationInfo>
            <ClockCircleOutlined style={{ fontSize: '18px', color: '#f59e0b' }} />
            <p>Vencimento: {formatDueDate(boletoInfo.dueDate)}</p>
          </BoletoExpirationInfo>
        )}
      </BoletoInfoBox>

      <BoletoButtonsContainer>
        {boletoInfo.pdf && (
          <BoletoPrimaryButton href={boletoInfo.pdf} target="_blank" rel="noopener noreferrer">
            <Receipt size={20} />
            Abrir PDF do Boleto
          </BoletoPrimaryButton>
        )}

        <BoletoSecondaryButton onClick={handleFinishAndRedirect}>Já realizei o pagamento</BoletoSecondaryButton>
      </BoletoButtonsContainer>

      <BoletoInstructions>
        <h4>Como pagar seu boleto:</h4>
        <ol>
          <li>Copie a linha digitável ou abra o PDF do boleto</li>
          <li>Acesse o app do seu banco ou internet banking</li>
          <li>Selecione a opção de pagamento de boleto</li>
          <li>Cole a linha digitável ou escaneie o código de barras</li>
          <li>Confirme os dados e finalize o pagamento</li>
        </ol>
      </BoletoInstructions>
    </BoletoContainer>
  )
}

// Constantes de desconto PIX
const PIX_DISCOUNT_PERCENTAGE = 5

// Função para calcular valor com desconto PIX
const applyPixDiscount = (amount: number): number => {
  const discount = amount * (PIX_DISCOUNT_PERCENTAGE / 100)
  return amount - discount
}

// Main Payment Component
export default function Payment({ onPaymentComplete, onBack }: PaymentProps) {
  const history = useHistory()
  const [order, setOrder] = useState<OrderData | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingPayment, setLoadingPayment] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('')

  const handlePaymentSuccess = () => {
    setShowSuccessModal(true)
  }

  const handlePaymentError = (message?: string) => {
    setPaymentErrorMessage(message || 'Ocorreu um erro ao processar seu pagamento. Por favor, tente novamente.')
    setShowErrorModal(true)
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    // Os componentes de pagamento limpam o localStorage antes do sucesso,
    // então o Cart precisa do id vindo do pedido já carregado aqui.
    if (onPaymentComplete) {
      onPaymentComplete(order?.id)
      return
    }
    history.push('/AreaAssociados/MinhaConta?pagina=pedidos-todos')
  }

  const handleErrorModalClose = () => {
    setShowErrorModal(false)
    setPaymentErrorMessage('')
  }

  const fetchOrderData = async () => {
    try {
      const orderId = getOrderId()
      if (!orderId) {
        toast.error('Pedido não encontrado')
        onBack?.()
        return
      }

      const response = await api.post(`/order_status/${orderId}`)
      if (response.data.success) {
        setOrder(response.data.order)
      }
    } catch (err) {
      console.error('Error fetching order:', err)
      toast.error('Erro ao carregar dados do pedido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrderData()
  }, [])

  const finishOrderByPaymentMethod = async ({ type, creditCard }: { type: string; creditCard?: CreditCardData }) => {
    try {
      setLoadingPayment(true)

      const originalAmount = Math.round(parseFloat(order?.amount || '0') * 100) // Converte para centavos

      // Aplica desconto de 5% para PIX
      const amount = type === 'pix' ? Math.round(applyPixDiscount(originalAmount)) : originalAmount

      let payments: any[] = []

      if (type === 'pix') {
        payments = [
          {
            paymentMethod: { type: 'pix' },
            amount
          }
        ]
      } else if (type === 'credit_card' && creditCard) {
        payments = [
          {
            paymentMethod: { type: 'credit_card' },
            card_number: creditCard.card_number?.replace(/\s/g, ''),
            card_name: creditCard.card_name,
            card_month_validate: creditCard.card_month_validate,
            card_year_validate: creditCard.card_year_validate,
            card_cvv: creditCard.card_cvv,
            installments: creditCard.installments || 1,
            remeberCard: false,
            card_value: amount,
            amount
          }
        ]
      } else if (type === 'boleto') {
        payments = [
          {
            paymentMethod: { type: 'boleto' },
            amount
          }
        ]
      }

      const payload = {
        order_id: order?.id,
        payments,
        utmParams: {}
      }

      const { data } = await api.post('/payments/charges', payload)

      // Processar resposta
      if (data.success && data.responses && data.responses.length > 0) {
        const paymentResponse = data.responses[0]
        const lastTransaction = paymentResponse.last_transaction

        if (type === 'pix' && lastTransaction?.success) {
          return {
            paymentInfos: {
              pix_qr_code: lastTransaction.qr_code_text || lastTransaction.qr_code || '',
              qr_code_url: lastTransaction.qr_code_url || '',
              status: lastTransaction.status || 'pending',
              charge_id: paymentResponse.id
            }
          }
        }

        if (type === 'credit_card') {
          return {
            paymentInfos: {
              status: lastTransaction?.success ? 'paid' : 'refused',
              charge_id: paymentResponse.id
            }
          }
        }

        if (type === 'boleto' && lastTransaction?.success) {
          return {
            paymentInfos: {
              boleto_line: lastTransaction.line || '',
              boleto_barcode: lastTransaction.barcode || lastTransaction.qr_code || '',
              boleto_pdf: lastTransaction.pdf || lastTransaction.url || '',
              boleto_due_date: lastTransaction.due_at || '',
              status: lastTransaction.status || 'pending',
              charge_id: paymentResponse.id
            }
          }
        }
      }

      throw new Error('Falha ao processar pagamento')
    } catch (error) {
      console.error('Erro ao processar pagamento:', error)
      throw error
    } finally {
      setLoadingPayment(false)
    }
  }

  const paymentMethods = [
    {
      id: 'credit_card',
      title: 'Cartão de Crédito',
      icon: <CreditCard size={24} />,
      description: 'Pague com seu cartão de crédito',
      hasDiscount: false
    },
    {
      id: 'pix',
      title: 'Pix',
      icon: <Wallet size={24} />,
      description: `${PIX_DISCOUNT_PERCENTAGE}% de desconto`,
      hasDiscount: true
    },
    {
      id: 'boleto',
      title: 'Boleto Bancário',
      icon: <Receipt size={24} />,
      description: 'Vencimento em 3 dias úteis',
      hasDiscount: false
    }
  ]

  const getPaymentAmount = (methodId: string): number => {
    const originalAmount = parseFloat(order?.amount || '0')
    if (methodId === 'pix') {
      return applyPixDiscount(originalAmount)
    }
    return originalAmount
  }

  const handleSelectMethod = (methodId: string) => {
    setSelectedMethod(methodId)
  }

  const handleBack = () => {
    if (selectedMethod) {
      setSelectedMethod(null)
    } else {
      onBack?.()
    }
  }

  const getSelectedMethodInfo = () => {
    const method = paymentMethods.find((m) => m.id === selectedMethod)
    return method || paymentMethods[0]
  }

  const renderPaymentForm = () => {
    if (!order) return null

    switch (selectedMethod) {
      case 'pix':
        return (
          <PixPayment
            finishOrderByPaymentMethod={finishOrderByPaymentMethod}
            loadingPayment={loadingPayment}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
          />
        )
      case 'credit_card':
        return (
          <CreditCardPayment
            order={order}
            finishOrderByPaymentMethod={finishOrderByPaymentMethod}
            loadingPayment={loadingPayment}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
          />
        )
      case 'boleto':
        return (
          <BoletoPayment
            finishOrderByPaymentMethod={finishOrderByPaymentMethod}
            loadingPayment={loadingPayment}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
          />
        )
      default:
        return null
    }
  }

  if (loading) {
    return <LoadingContainer>Carregando...</LoadingContainer>
  }

  if (!order) {
    return <ErrorContainer>Nenhum pedido encontrado</ErrorContainer>
  }

  return (
    <CartContainer>
      <CartHeader>
        <CartTitle>Pagamento</CartTitle>
      </CartHeader>

      {!selectedMethod ? (
        <PaymentContainer>
          <h2>Escolha como deseja pagar</h2>
          <PaymentSelector>
            {paymentMethods.map((method) => (
              <PaymentOption
                key={method.id}
                selected={selectedMethod === method.id}
                onClick={() => handleSelectMethod(method.id)}>
                <PaymentMethodIcon>{method.icon}</PaymentMethodIcon>
                <PaymentMethodInfo>
                  <h3>{method.title}</h3>
                  <p style={method.hasDiscount ? { color: '#02b0bb', fontWeight: 600 } : undefined}>
                    {method.description}
                  </p>
                </PaymentMethodInfo>
                <PaymentAmount>
                  {method.hasDiscount && (
                    <span
                      style={{
                        textDecoration: 'line-through',
                        color: '#9ca3af',
                        fontSize: '0.875rem',
                        marginRight: '0.5rem'
                      }}>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(parseFloat(order.amount))}
                    </span>
                  )}
                  <span style={method.hasDiscount ? { color: '#02b0bb' } : undefined}>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(getPaymentAmount(method.id))}
                  </span>
                </PaymentAmount>
              </PaymentOption>
            ))}
          </PaymentSelector>
        </PaymentContainer>
      ) : (
        <PaymentContainer>
          <SelectedMethodHeader>
            <BackButton onClick={handleBack}>
              <ArrowLeft size={20} />
              Voltar
            </BackButton>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {getSelectedMethodInfo().icon}
              <span>{getSelectedMethodInfo().title}</span>
            </div>
          </SelectedMethodHeader>
          {renderPaymentForm()}
        </PaymentContainer>
      )}

      {/* Modal de Sucesso */}
      <StatusModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        icon={<CheckCircleOutlined />}
        title="Pagamento realizado com sucesso!"
        text="Seu pagamento foi confirmado. Agora precisamos dos documentos do seu pedido para dar sequência."
        textButton="Enviar documentos"
        variant="success"
        onClickButton={handleSuccessModalClose}
      />

      {/* Modal de Erro */}
      <StatusModal
        isOpen={showErrorModal}
        onClose={handleErrorModalClose}
        icon={<RiErrorWarningFill />}
        title="Erro no pagamento"
        text={paymentErrorMessage}
        textButton="Tentar novamente"
        variant="error"
        onClickButton={handleErrorModalClose}
      />
    </CartContainer>
  )
}
